import { ClipboardButton } from '@calendly/clipboard';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Loader } from '@calendly/ui/components/loader';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { SwitchInput } from '@calendly/ui/components/switch-input';
import { VisuallyHidden } from '@calendly/ui/components/visually-hidden';
import { SelectField } from '@calendly/ui/form/select-field';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { useInterval } from '@calendly/ui/hooks/use-interval';
import classNames from 'classnames';
import { useEffect, useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BaseErrorMessage from 'components/core/error_message';
import { CopyText, HelpText, SubTitle } from 'features/organization_settings/shared/styles';
import { useBooleanState } from 'hooks/useBooleanState';
import { baseUrlSelector, bearerTokensSelector, isConfiguredSelector, isLoadingSelector, isSyncErrorSelector, isSyncingSelector, maximumBearerTokensSelector, tokenDurationOptionsSelector } from '../selectors';
import { fetchScimSettings, generateBearerToken, toggleConfiguration } from '../thunks';
import { DisablePopup } from './disable_popup';
import { TokensTable } from './tokens_table';
const syncerCss = "s1c8nbmt";
const Syncer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(syncerCss, props.className)} />;
});
const syncerLoaderCss = "s7coto9";
const SyncerLoader = forwardRef((props, ref) => <Loader ref={ref} {...props} className={classNames(syncerLoaderCss, props.className)} />);
const switchLabelCss = "s12q6ca8";
const SwitchLabel = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'label';
  return <Component ref={ref} {...restProps} className={classNames(switchLabelCss, props.className)} />;
});
const switchCss = "s18ty4ph";
const Switch = forwardRef((props, ref) => <SwitchInput ref={ref} {...props} className={classNames(switchCss, props.className)} />);
const switchPrimaryLabelTextCss = "s1usku4m";
const SwitchPrimaryLabelText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(switchPrimaryLabelTextCss, props.className)} />;
});
const provisionedSectionCss = "p888nss";
const ProvisionedSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(provisionedSectionCss, props.className)} />;
});
const sectionCss = "s6ryn3u";
const Section = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(sectionCss, props.className)} />;
});
const flexSectionIsPhoneCss = "fjy7c6d";
const flexSectionCss = "fnfu170";
const FlexSection = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <Section ref={ref} {...props} className={classNames(flexSectionCss, {
    [flexSectionIsPhoneCss]: isPhone
  }, props.className)} />;
});
const generateTokenIsPhoneCss = "gwzg250";
const generateTokenCss = "g1d55kai";
const GenerateToken = forwardRef((props, ref) => {
  const isPhone = useDeviceType('isPhone');
  return <Button ref={ref} {...props} className={classNames(generateTokenCss, {
    [generateTokenIsPhoneCss]: isPhone
  }, props.className)} />;
});
const tokenDurationSelectIsTabletUpCss = "t1ultn8y";
const tokenDurationSelectCss = "t7yebii";
const TokenDurationSelect = forwardRef((props, ref) => {
  const isTabletUp = useDeviceType('isTabletUp');
  return <SelectField ref={ref} {...props} className={classNames(tokenDurationSelectCss, {
    [tokenDurationSelectIsTabletUpCss]: isTabletUp
  }, props.className)} />;
});
const flexEndSectionCss = "f13wbpta";
const FlexEndSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(flexEndSectionCss, props.className)} />;
});
const errorMessageCss = "eub5axm";
const ErrorMessage = forwardRef((props, ref) => <BaseErrorMessage ref={ref} {...props} className={classNames(errorMessageCss, props.className)} />);
const labelCss = "l1iosfui";
const Label = forwardRef((props, ref) => {
  const as = 'label';
  return <SubTitle ref={ref} {...props} as={as} className={classNames(labelCss, props.className)} />;
});
const MailIdpSupportLink = () => <MinimalButton decoration="danger" as="a" href="mailto:idpsupport@calendly.com">
    idpsupport@calendly.com
  </MinimalButton>;
export const ScimConfiguration = ({
  onBackClick
}) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(isLoadingSelector);
  const isSyncing = useSelector(isSyncingSelector);
  const isSyncError = useSelector(isSyncErrorSelector);
  const isConfigured = useSelector(isConfiguredSelector);
  const maximumBearerTokens = useSelector(maximumBearerTokensSelector);
  const bearerTokens = useSelector(bearerTokensSelector);
  const baseUrl = useSelector(baseUrlSelector);
  const canGenerateToken = bearerTokens.length < maximumBearerTokens && isConfigured;
  const [disablePopupIsOpen, openDisablePopup, closeDisablePopup] = useBooleanState(false);
  const tokenDurationOptions = useSelector(tokenDurationOptionsSelector);
  const [tokenDuration, setTokenDuration] = useState();
  const handleGenerateTokenClick = () => dispatch(generateBearerToken(tokenDuration));
  const handleToggleConfiguration = () => dispatch(toggleConfiguration());
  useEffect(() => {
    if (typeof tokenDuration === 'undefined' && tokenDurationOptions.length > 0) setTokenDuration(tokenDurationOptions[0].value);
  }, [tokenDuration, tokenDurationOptions]);
  useInterval(() => {
    dispatch(fetchScimSettings());
  }, isSyncing ? 5000 : null);
  if (isLoading) {
    return <Loader />;
  }
  if (isSyncing) {
    return <Syncer>
        <p><strong>{I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.syncing.heading')}</strong></p>
        <SyncerLoader />
        <p>{I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.syncing.time_estimation')}</p>
        <p>{I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.syncing.info_text')}</p>
      </Syncer>;
  }
  return <>
      <DisablePopup isOpen={disablePopupIsOpen} onClose={closeDisablePopup} onDisable={handleToggleConfiguration} />
      <SwitchLabel>
        <Switch aria-label={I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.switch')} checked={isConfigured} onChange={() => {
        if (isConfigured) {
          openDisablePopup();
        } else {
          dispatch(toggleConfiguration());
        }
      }} />
        <SwitchPrimaryLabelText>
          {I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.switch')}
        </SwitchPrimaryLabelText>
      </SwitchLabel>
      {isSyncError && <ErrorMessage message={HtmlParser(I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.syncing.error'), {
      MailIdpSupportLink
    })} />}

      <ProvisionedSection>
        <Section disabled={!isConfigured}>
          {!canGenerateToken && <VisuallyHidden id="sectionDisabledLabel">
              {I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.token_durations.section_disabled_label')}
            </VisuallyHidden>}
          <SubTitle>{I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.base_url.label')}</SubTitle>
          <HelpText>{I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.base_url.help_text')}</HelpText>
          <ClipboardButton decoration="outline" size="small" content={baseUrl} disabled={!isConfigured}>
            {I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.base_url.copy')}
          </ClipboardButton>
          {isConfigured && <CopyText>{baseUrl}</CopyText>}
        </Section>
        <Section disabled={!isConfigured}>
          <SubTitle>{I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.generate_bearer_token.label')}</SubTitle>
          <HelpText>
            {I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.generate_bearer_token.help_text')}
          </HelpText>
          <Label id="tokenLabel">
            {I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.token_durations.expiration_label')}
          </Label>
          <FlexSection>
            <TokenDurationSelect disabled={!canGenerateToken} aria-labelledby="tokenLabel" aria-describedby={canGenerateToken ? undefined : 'sectionDisabledLabel'} value={tokenDuration} onChange={setTokenDuration} options={tokenDurationOptions} />
            <GenerateToken decoration="outline" onClick={handleGenerateTokenClick} size="small" disabled={!canGenerateToken}>
              {I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.generate_bearer_token.copy')}
            </GenerateToken>
          </FlexSection>
          {!canGenerateToken && <HelpText>
                {I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.generate_bearer_token.max')}
              </HelpText>}
        </Section>
        <Section>
          <TokensTable bearerTokens={bearerTokens} />
        </Section>
        {onBackClick && <FlexEndSection>
            <Button decoration="ghost" onClick={onBackClick}>{I18n.t('publisher.action.go_back')}</Button>
          </FlexEndSection>}
      </ProvisionedSection>
    </>;
};

require("./scim_configuration.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./scim_configuration.js");